import { useState, useMemo } from 'react';
import DotPagination from './DotPagination';

interface Skill {
    id: number;
    skill_id: number;
    skill_name: string;
    proficiency_level: string;
    years_of_experience: number;
    certified: boolean;
}

interface SkillsSectionProps {
    skills: Skill[];
    loading: boolean;
    error: any;
}

const ITEMS_PER_PAGE = 5;

const SkillsSection = ({ skills, loading, error }: SkillsSectionProps) => {
    const [currentPage, setCurrentPage] = useState(0);

    // Calculate pagination
    const totalPages = Math.ceil(skills.length / ITEMS_PER_PAGE);
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentSkills = useMemo(() => skills.slice(startIndex, endIndex), [skills, startIndex, endIndex]);

    // Reset to page 1 when skills change
    useMemo(() => {
        setCurrentPage(0);
    }, [skills.length]);
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 leftsidebar-card-dv">
            <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Skills</h3>
                </div>
                
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="animate-pulse">
                                <div className="h-8 bg-surface-bg-secondary rounded-lg" />
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="text-center py-4 text-danger-active text-sm">
                        Error loading skills
                    </div>
                ) : skills.length === 0 ? (
                    <div className="text-center py-8">
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-text-text-secondary text-sm">No skills found</p>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {currentSkills.map((skill, index) => (
                                <div 
                                    key={skill.id} 
                                    className="group relative"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-lg blur opacity-0 group-hover:opacity-75 transition-opacity duration-300"></div>
                                    <span className="relative inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 text-gray-700 dark:text-gray-200 font-medium text-sm rounded-lg border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 shadow-sm hover:shadow-md skills-items-dv">
                                        <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        {skill.skill_name}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <DotPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SkillsSection;
export type { Skill };
